import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface OrderData {
  schoolId: string;
  occasion: string;
  recipientName: string;
  email: string;
  customMessage: string;
  deliveryDate: string;
  price: number;
  timestamp: string;
}

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const storedOrderData = localStorage.getItem('orderData');
    if (storedOrderData) {
      setOrderData(JSON.parse(storedOrderData));
    } else {
      // Redirect to home if no order data
      navigate('/');
    }
  }, [navigate]);

  const handlePayment = async () => {
    setIsProcessing(true);
    
    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In a real implementation, this would integrate with Stripe
      console.log('Processing payment for:', orderData);
      
      // Navigate to confirmation page
      navigate('/confirmation');
    } catch (error) {
      console.error('Payment failed:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!orderData) {
    return (
      <div className="min-h-screen bg-midnight py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-lg text-gray-300">Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-midnight py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-white mb-4">Payment</h1>
          <p className="text-lg text-gray-300">Complete your order with secure payment</p>
        </div>
        
        <div className="card">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-black mb-4">Order Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-white">Recipient:</span>
                <span className="font-medium text-black">{orderData.recipientName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Occasion:</span>
                <span className="font-medium text-black capitalize">{orderData.occasion}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Personalized Video:</span>
                <span className="text-black">${orderData.price}.00</span>
              </div>
              <div className="border-t border-black pt-3">
                <div className="flex justify-between font-semibold text-lg">
                  <span className="text-black">Total</span>
                  <span className="text-black">${orderData.price}.00</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-black pt-6">
            <h3 className="text-lg font-semibold text-black mb-4">Payment Method</h3>
            <div className="bg-goldie p-4 border-2 border-black">
              <p className="text-black">Stripe payment integration will be implemented here</p>
              <p className="text-sm text-white mt-2">For now, this is a demo payment flow</p>
            </div>
          </div>
          
          <button
            onClick={handlePayment}
            disabled={isProcessing}
            className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? 'Processing Payment...' : `Pay $${orderData.price}.00`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage; 